import { trackOpenWithFaireOwfApplicationFieldValueChange } from "@faire/web-api/events/openWithFaire/actionUnknown/owfApplicationFieldValueChange";
import { IDisplayableMoneyRange } from "@faire/web-api/indigofair/data/IDisplayableMoneyRange";
import { IRetailer } from "@faire/web-api/indigofair/data/IRetailer";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api/indigofair/data/IRetailerLaunchWithFaireApplication";
import { getStoreOpeningDateOptions } from "@faire/web/common/consts/STORE_OPENING_DATE_OPTIONS";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { intlDate } from "@faire/web/common/localization/date-fns/intlDate";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { IOption } from "@faire/web/slate/Input/Dropdown";
import addDays from "date-fns/addDays";
import addMonths from "date-fns/addMonths";
import isSameMonth from "date-fns/isSameMonth";
import startOfMonth from "date-fns/startOfMonth";
import subYears from "date-fns/subYears";

import { getSettingGlobalOrNamSignUpFriction } from "@faire/retailer/views/SignUp/utils/getSettingGlobalOrNamSignUpFriction";

import {
  OwFApplicationKey,
  OwFFormFieldKey,
  PSEUDO_FIELD_NAMES,
} from "./consts";
import { FORM_VERSION } from "./useOwFForm";
import { OwFTier } from "./useOwFIDVTierLogic";

const getAlreadyOpenOpeningDateOption = (
  strictLocalize: StrictLocalizeFunction,
  openingDate: number
) => {
  return getAlreadyOpenStoreOpeningDateOptions(strictLocalize).find((option) =>
    isSameMonth(Number(option.value), addDays(openingDate, 1).getTime())
  )?.value;
};

const getOpeningSoonOpeningDateOption = (
  strictLocalize: StrictLocalizeFunction,
  openingDate: number,
  includeAlreadyOpen: boolean = false
) => {
  if (openingDate < 0) {
    return getStoreOpeningDateOptions(strictLocalize, includeAlreadyOpen)
      .find((option) => option.value === openingDate)
      ?.value?.toString();
  }

  return getStoreOpeningDateOptions(strictLocalize, includeAlreadyOpen)
    .find((option) =>
      isSameMonth(Number(option.value), addDays(openingDate, 1).getTime())
    )
    ?.value?.toString();
};

export const getDefaultStoreOpeningDate = (
  strictLocalize: StrictLocalizeFunction,
  openingDate: number | undefined,
  reportedCreationTime: keyof typeof IRetailer.ReportedCreationTime | undefined
): string | undefined => {
  // This check is to support eligible retailers that signed up before we started collecting openingDate.
  if (!openingDate) {
    return undefined;
  }

  if (
    reportedCreationTime === IRetailer.ReportedCreationTime.STARTED_THIS_YEAR
  ) {
    return getAlreadyOpenOpeningDateOption(strictLocalize, openingDate);
  }

  if (
    reportedCreationTime === IRetailer.ReportedCreationTime.STARTED_ONE_YEAR_AGO
  ) {
    return getAlreadyOpenLastYearStoreOpeningDateOptions().find((option) =>
      isSameMonth(Number(option.value), addDays(openingDate, 1).getTime())
    )?.value;
  }

  return getOpeningSoonOpeningDateOption(strictLocalize, openingDate);
};

export const displayableMoneyRangeToOption = (
  range: IDisplayableMoneyRange,
  index: number
): IOption => {
  return {
    label: range.display_text ?? "",
    // Use index instead of a text value to get corresponding range afterwards
    value: index.toString(),
  };
};

const getMonthDropdownOptions = (startingDate: Date, months: Array<number>) => {
  const startingDateAdjusted =
    startingDate.getMonth() !== startingDate.getUTCMonth()
      ? addDays(startOfMonth(addDays(startingDate, 1)), 1)
      : addDays(startOfMonth(startingDate), 1);

  const monthsOptions = months.map((addAmount) => {
    return addMonths(startingDateAdjusted, addAmount).getTime();
  });

  return monthsOptions;
};

export const getPrevious12MonthsDateOptions = (
  strictLocalize: StrictLocalizeFunction
): Array<IOption<string>> => {
  const monthsOptions = getMonthDropdownOptions(subYears(new Date(), 1), [
    ...Array(12 + 1).keys(),
  ]);
  return monthsOptions.reverse().map((dateTimestamp, index) => {
    return {
      label:
        index === 0
          ? strictLocalize({ defaultMessage: "This month" })
          : // Safe to capitalize first letter here as we know it is the
            // name of the month to start the sentence
            capitalizeFirstLetter(intlDate(dateTimestamp, "MMMM yyyy")),
      value: dateTimestamp.toString(),
    };
  });
};

export const getThisYearsMonthDateOptions = (
  strictLocalize: StrictLocalizeFunction
): Array<IOption<string>> => {
  const startOfThisYear = new Date(new Date().getFullYear(), 0, 1);
  const monthsOptions = getMonthDropdownOptions(startOfThisYear, [
    ...Array(new Date().getUTCMonth() + 1).keys(),
  ]);
  return monthsOptions.reverse().map((dateTimestamp, index) => {
    return {
      label:
        index === 0
          ? strictLocalize({ defaultMessage: "This month" })
          : // Safe to capitalize first letter here as we know it is the
            // name of the month to start the sentence
            capitalizeFirstLetter(intlDate(dateTimestamp, "MMMM yyyy")),
      value: dateTimestamp.toString(),
    };
  });
};

export const getPreviousYearsMonthDateOptions = (): Array<IOption<string>> => {
  const startOfLastYear = subYears(new Date(new Date().getFullYear(), 0, 1), 1);
  const monthsOptions = getMonthDropdownOptions(startOfLastYear, [
    ...Array(12).keys(),
  ]);
  return monthsOptions.map((dateTimestamp) => {
    return {
      label:
        // Safe to capitalize first letter here as we know it is the
        // name of the month to start the sentence
        capitalizeFirstLetter(intlDate(dateTimestamp, "MMMM yyyy")),
      value: dateTimestamp.toString(),
    };
  });
};

export const getStoreOpeningDateOptionsWithStringValue = (
  ...args: Parameters<typeof getStoreOpeningDateOptions>
): Array<IOption<string>> => {
  return getStoreOpeningDateOptions(...args)
    .map((option) => {
      if (!option.value) {
        return;
      }
      return {
        // Safe to capitalize first letter here as we know it is the
        // name of the month to start the sentence
        label: capitalizeFirstLetter(option.label),
        value: option.value.toString(),
      };
    })
    .filter(isNotUndefined);
};

const capitalizeFirstLetter = (dateString: string) => {
  return dateString.charAt(0).toUpperCase() + dateString.slice(1);
};

export const getAlreadyOpenStoreOpeningDateOptions = (
  strictLocalize: StrictLocalizeFunction
) => getPrevious12MonthsDateOptions(strictLocalize);

export const getAlreadyOpenLastYearStoreOpeningDateOptions = () =>
  getPreviousYearsMonthDateOptions();

export const isNotPseudoKey = (
  key: OwFFormFieldKey
): key is OwFApplicationKey =>
  !(PSEUDO_FIELD_NAMES as readonly string[]).includes(key as string);

export const shouldShowOwfOpeningMonth = (
  creationYear: keyof typeof IRetailer.ReportedCreationTime | undefined
) => {
  switch (creationYear) {
    case IRetailer.ReportedCreationTime.OPENING_SOON:
      // we always show the opening month for stores that are opening soon
      return true;
    case IRetailer.ReportedCreationTime.STARTED_THIS_YEAR:
      // we don't need to collect opening month for this year, they all are OwF eligible
      return false;
    case IRetailer.ReportedCreationTime.STARTED_ONE_YEAR_AGO:
      if (getSettingGlobalOrNamSignUpFriction()) {
        // some months are eligible for OwF but not all, so we need to collect opening month
        return true;
      }
      return false;
    case IRetailer.ReportedCreationTime.STARTED_BETWEEN_5_AND_2_YEAR_AGO:
    case IRetailer.ReportedCreationTime.BEFORE_5_YEAR_AGO:
    default:
      return false;
  }
};

export const getNMIFlowRequiredStoreFieldKeys = (
  retailerBusinessType: keyof typeof IRetailer.BusinessCategory | undefined,
  leaseSigned:
    | keyof typeof IRetailerLaunchWithFaireApplication.LeaseSigned
    | undefined,
  isEuropeNonUk: boolean
): OwFFormFieldKey[] => {
  let keys: OwFFormFieldKey[] = [];
  const squareFootageOrMeterageField = isEuropeNonUk
    ? "store_square_meterage"
    : "store_square_footage";

  switch (retailerBusinessType) {
    case IRetailer.BusinessCategory.BRICK_AND_MORTAR:
      keys = keys.concat(["lease_signed"]);

      if (leaseSigned === IRetailerLaunchWithFaireApplication.LeaseSigned.YES) {
        keys = keys.concat([
          "lease_length",
          "monthly_rent",
          squareFootageOrMeterageField,
        ]);
      } else if (
        leaseSigned === IRetailerLaunchWithFaireApplication.LeaseSigned.OWNED
      ) {
        keys = keys.concat(["store_address", squareFootageOrMeterageField]);
      }

      break;
    case IRetailer.BusinessCategory.POP_UP:
      keys = keys.concat([
        "popup_days_per_week",
        "popup_hours_open",
        squareFootageOrMeterageField,
      ]);

      break;
    case IRetailer.BusinessCategory.ONLINE_ONLY:
      keys = keys.concat(["store_hours_commitment"]);

      break;
    default:
      break;
  }

  return keys;
};

export const getSoftCreditCheckFlowRequiredStoreFieldKeys = (
  isRetailerOther: boolean,
  retailerBusinessType: keyof typeof IRetailer.BusinessCategory | undefined,
  showDetailedStoreQuestions: boolean,
  formData: IRetailerLaunchWithFaireApplication
): OwFFormFieldKey[] => {
  let keys: OwFFormFieldKey[] = isRetailerOther ? ["best_store_type"] : [];

  switch (retailerBusinessType) {
    case IRetailer.BusinessCategory.BRICK_AND_MORTAR:
      keys.push("lease_signed");

      if (showDetailedStoreQuestions) {
        keys.push("store_address");
      }

      break;
    case IRetailer.BusinessCategory.POP_UP:
      keys = keys.concat([
        "popup_location_checkboxes",
        "popup_locations",
        "popup_lease",
      ]);

      break;
    case IRetailer.BusinessCategory.ONLINE_ONLY:
      keys = keys.concat(["online_has_store_website"]);

      if (
        formData.online_has_store_website ===
        IRetailerLaunchWithFaireApplication.HasStoreWebsite.HAS_WEBSITE
      ) {
        keys.push("website_url");
      } else if (
        formData.online_has_store_website ===
        IRetailerLaunchWithFaireApplication.HasStoreWebsite.NO_WEBSITE
      ) {
        keys.push("online_website_proof_documents");
      }

      break;
    default:
      break;
  }

  return keys;
};

export const trackOwFApplicationFormFieldValueChange = (
  fieldName: OwFFormFieldKey,
  formVersion: keyof typeof FORM_VERSION | keyof typeof OwFTier,
  isSignUp: string
) => {
  const trackedFieldName = getTrackedFieldName(formVersion, fieldName);

  trackOpenWithFaireOwfApplicationFieldValueChange(
    trackedFieldName,
    formVersion,
    isSignUp
  );
};

const getTrackedFieldName = (
  formVersion: keyof typeof FORM_VERSION | keyof typeof OwFTier,
  fieldName: OwFFormFieldKey
) => {
  if (
    formVersion === FORM_VERSION.SOFT_CREDIT_CHECK &&
    fieldName === "credit_check_authorized"
  ) {
    return "soft_credit_check_authorized";
  }

  return fieldName;
};
